// Vendor Item Purchase Request - This is the Desktop Side (Part 2)
// VendorItemScan.js is Mobile Side (Part 1)
import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Input, Label, Row, Table } from "reactstrap";
import _ from "lodash";
import { FilterSelect, ServiceThreadSpinner, SpinnerButton } from "./";
import { api, filterHelpers } from "../utils";
import { DesktopContainer } from "../layouts";
import { toast } from "react-toastify";

export default function VendorItemRequest() {
   const PALLET_MODEL = { id: 0, barcode: "", delete: false, netWeight: 0, pallet: "", parentId: 0, poNum: "" };
   const SITE_OPTION = { value: "0", label: "--- Select ---" };
   const ITEM_OPTION = { value: "0", label: "--- Select ---", site: "" };
   const [hdr, setHeader] = useState({});                 // Data for the header record
   const [hdrExtra, setHeaderExtra] = useState({});       // Extra data from the request header
   const [details, setDetails] = useState([]);            // Data for the detail records grid
   const [isLoading, setIsLoading] = useState(true);      // Show the spinner if loading or refreshing data
   const [itemList, setItemList] = useState([]);          // Item options unfiltered
   const [items, setItems] = useState([]);                // Item options filtered for selected site   
   const [loaded, setLoaded] = useState(false);           // Has the initial page load completed?
   const [selectedItem, setSelectedItem] = useState("0"); // Item filter selection
   const [selectedSite, setSelectedSite] = useState("0"); // Site filter selection
   const [sites, setSites] = useState([]);                // Site filter options
   const [pallets, setPallets] = useState(null);          // Pallets for saving
   const [working, setWorking] = useState(false);         // Completing vendor purchase request
   const [filters, setFilters] = useState([
      { filterName: "site", value: SITE_OPTION },
      { filterName: "item", value: ITEM_OPTION },
   ]);

   useEffect(() => {
      document.title = `Buy Consigned Item`
      initPage();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      if (loaded && selectedSite !== "0" && selectedItem !== "0") {
         refreshItems();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedItem]);

   useEffect(() => {
      if (details?.length > 0)
         initPallets();
      else
         setPallets(null);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [details]);

   function CompleteRequest() {
      let quit = false;
      _.forEach(pallets, function (value) {
         if (quit) {
            setWorking(false);
            return;
         }
         let item = value;
         if (_.trim(item.pallet) === "" && item.delete === false) {
            toast.error("Pallet numbers are required for all labels not being removed");
            quit = true;
            setWorking(false);
            return;
         }
      });
      if (quit) {
         setWorking(false);
         return;
      }

      api
         .post("Purchasing/CompleteVendorPurchase", pallets)
         .then((response) => {
            if (response) {
               if (response.data.success) {
                  window.location.reload(); // load page from scratch again
               } else {
                  toast.error(response.data.message)
               }
            }
            else {
               toast.error("Request Completion Failed");
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setWorking(false);
         });
   }

   const fixItemSubs = async (id) => {
      let newList = [ITEM_OPTION]; // If no children found then set the default option
      var subs = _.pickBy(itemList, (e) => _.includes([id], e.site));
      if (subs) {
         // There are child items for the selected site
         newList = _.concat(
            [ITEM_OPTION],
            _.map(subs, (x) => ({ value: x.value, label: x.label }))
         );
         setItems(newList);
      }
      return newList;
   };

   function formatItems(data) {
      let itemArr = data.items;
      let list = _.map(itemArr, (x) => {
         return { label: x.itemWithDesc, value: x.item, site: x.siteId }
      });
      return list;
   }

   function formatSites(data) {
      let siteArr = data.sites;
      let list = _.concat(
         [SITE_OPTION],
         _.map(siteArr, (s) => ({ value: s.siteId, label: s.siteWithDesc }))
      );
      return list;
   }

   const handleFocus = (event) => event.target.select();

   function initPage() {
      api
         .fetch("Purchasing/InitVendorPurchase")
         .then((response) => {
            if (response) {
               if (response.data.success) {
                  setItemList(formatItems(response.data));
                  setSites(formatSites(response.data));
               } else {
                  toast.error(response.data.message);
               }
            } else {
               toast.error("Page load failed");
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setIsLoading(false);
            setLoaded(true);
         });
   }

   function initPallets() {
      let labels = [];
      _.forEach(details, function (value) {
         let item = value;
         let changes = Object.assign({}, PALLET_MODEL);
         changes.id = item.id;
         changes.barcode = item.barcode;
         changes.netWeight = item.netWeight;
         changes.netWeightAsString = item.netWeightAsString;
         changes.pallet = item.pallet;
         changes.parentId = item.parentId
         changes.poNum = item.poNum;
         labels.push(changes);
      });
      if (labels.length > 0)
         setPallets(labels);
   }

   function onItemChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);
      setSelectedItem(newFilterValue.value);
   }

   function onPalletChange(value, idToUpdate) {
      setPallets((prev) =>
         prev.map((p) => p.id === idToUpdate ? { ...p, pallet: value } : p)
      );
   }

   function onPalletDeleteToggle(idToUpdate) {
      setPallets((prev) =>
         prev.map((p) => p.id === idToUpdate ? { ...p, delete: !p.delete } : p)
      );
   }

   const onSiteChange = async (newFilterValue) => {
      // Fill in the item drop-down with items for the selected site
      const s = _.find(sites, (x) => x.value === newFilterValue.value);
      let newSubList = await fixItemSubs(newFilterValue.value);

      // Set the filters
      const subFilter = _.find(newSubList, (s) => s.value === "0");
      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "site",
         value: s,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "item",
         value: subFilter,
      });
      setFilters(newFilters);

      // Set the selected options
      setSelectedSite(newFilterValue.value);
      setSelectedItem("0");
   };

   function refreshItems() {
      let payload = {
         Item: selectedItem || "0",
         SiteId: selectedSite || "0",
         SortDirection: "",
         SortField: ""
      };

      if (payload.SiteId === "0") {
         toast.warn("Please select a site");
         return;
      }
      if (payload.Item === "0") {
         toast.warn("Please select an item");
         return;
      }

      setIsLoading(true);
      api
         .post("Purchasing/GetVendorPurchase", payload)
         .then((response) => {
            if (response) {
               if (response.data.success) {
                  setHeader(response.data.item);
                  setHeaderExtra(response.data.extras);
                  setDetails(response.data.details);
               } else {
                  toast.error(response.data.message);
               }
            } else {
               toast.error("Error Loading the Consigned Purchase Request");
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setIsLoading(false);
         });
   }

   return (
      <DesktopContainer screenName="Consigned Item Request">
         <div className="filter-header mt-1 ml-3 justify-content-center align-items-center">
            <div className="align-items-center divCenter800px">
               <Row className="align-items-center">
                  <Col xs="3">
                     <Label check className="mx-2 float-end" style={{ color: "white", fontSize: "1.25rem" }}>
                        Site:
                     </Label>
                  </Col>
                  <Col xs="7">
                     <FilterSelect
                        filterName="site"
                        options={sites}
                        value={filterHelpers.getValue(filters, "site")}
                        onChangeCallback={(e) => onSiteChange(e)}
                     />
                  </Col>
               </Row>
               <Row className="mt-3 align-items-center">
                  <Col xs="3">
                     <Label check className="ms-2 float-end" style={{ color: "white", fontSize: "1.25rem" }}>
                        Item:
                     </Label>
                  </Col>
                  <Col xs="7">
                     <FilterSelect
                        filterName="item"
                        options={items}
                        value={filterHelpers.getValue(filters, "item")}
                        onChangeCallback={(e) => onItemChange(e)}
                     />
                  </Col>
               </Row>
            </div>
         </div>

         {hdr && hdr.item && selectedItem !== "0" ? (
            <ServiceThreadSpinner on={isLoading}>
               <br />
               <Card className="dashboard-card" style={{ minWidth: '960px' }}>
                  <CardBody>
                     <div><span className="h2 me-2">Enter Pallet Numbers</span></div>
                     <hr />
                     <div className="justify-content-center">
                        <Row className="mt-4 justify-content-center">
                           <Table size="sm" className="data-table width1200px">
                              <tbody>
                                 <tr>
                                    <td style={{ borderBottom: "none" }}>
                                       <Label className="ms-2" style={{ fontSize: "1.25rem" }}>
                                          ABC: {hdr.abcDisplay}
                                       </Label>
                                    </td>
                                    <td style={{ borderBottom: "none" }}>
                                       <Label className="ms-2" style={{ fontSize: "1.25rem" }}>
                                          Class: {hdr.itemClass}
                                       </Label>
                                    </td>
                                    <td style={{ borderBottom: "none" }}>
                                       <Label className="ms-2" style={{ fontSize: "1.25rem" }}>
                                          On Hand: {hdr.onHandAsString}
                                       </Label>
                                    </td>
                                    <td style={{ borderBottom: "none" }}>
                                       <Label className="ms-2" style={{ fontSize: "1.25rem" }}>
                                          Exp. Weight: {hdrExtra.weightAsString}
                                       </Label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td colSpan={2} style={{ borderBottom: "none" }}>
                                       <Label className="ms-2" style={{ fontSize: "1.25rem" }}>
                                          Entered: {hdrExtra.enteredWithBy}
                                       </Label>
                                    </td>
                                    <td colSpan={2} style={{ borderBottom: "none" }}>
                                       <Label className="ms-2" style={{ fontSize: "1.25rem" }}>
                                          Vendor Item: {hdr.vendorItem}
                                       </Label>
                                    </td>
                                 </tr>
                              </tbody>
                           </Table>
                        </Row>
                     </div>

                     {pallets ? (
                        <div className="justify-content-center">
                           <br />
                           <Row className="justify-content-center">
                              <Table size="sm" className="data-table width800px">
                                 <thead className="table-header">
                                    <tr>
                                       <th>Barcode</th>
                                       <th>Net Wt</th>
                                       <th>PO #</th>
                                       <th className="width225px">Pallet #</th>
                                       <th className="width80px">Remove</th>
                                       <th></th>
                                    </tr>
                                 </thead>
                                 <tbody>
                                    {_.map(pallets, (datum, index) => {
                                       return (
                                          <tr key={index}>
                                             <td className="data-cell">{datum.barcode}</td>
                                             <td className="data-cell">{datum.netWeightAsString}</td>
                                             <td className="data-cell">{datum.poNum}</td>
                                             <td className="data-cell">
                                                <Input
                                                   type="text"
                                                   value={datum.pallet || ""}
                                                   onFocus={handleFocus}
                                                   onChange={(e) => onPalletChange(e.target.value, datum.id)}
                                                />
                                             </td>
                                             <td className="data-cell" style={{textAlign: "center", verticalAlign: "middle"}}>
                                                <Input type="checkbox"
                                                   checked={datum.delete}
                                                   className="me-2"
                                                   onChange={() => onPalletDeleteToggle(datum.id)}
                                                />
                                             </td>
                                             <td className=""></td>
                                          </tr>
                                       );
                                    })}
                                 </tbody>
                              </Table>
                           </Row>
                           <br />
                           <Row className="mt-3 d-flex justify-content-center">
                              <SpinnerButton
                                 className="shredButton btn-md width265px"
                                 onClick={() => {
                                    setWorking(true);
                                    CompleteRequest();
                                 }}
                                 on={working}
                                 disabled={working}>
                                 Complete Purchase Request
                              </SpinnerButton>
                           </Row>
                        </div>
                     ) : null}
                  </CardBody>
               </Card>
            </ServiceThreadSpinner>
         ) : null}
      </DesktopContainer>
   );
}
